import { Alert, Modal, Spin, Table, Tag, Typography } from 'antd'
import { useState } from 'react'
import { useRelinkCandidatesQuery, useRelinkQuickBooksInvoiceMutation } from './quickBooksResyncQueries'
import {
  RelinkCandidateRow,
  candidateListTruncatedMessage,
  getNoCandidatesMessage,
  getProposedCandidateId,
  getRelinkCandidatesErrorMessage,
  getRelinkErrorMessage,
  getRelinkSuccessMessage,
  toRelinkCandidateRows,
} from './quickBooksResyncModel'

const { Paragraph } = Typography

type Props = {
  tenantId: number
  invoiceId: string
  invoiceNumber: string
  onClose: () => void
  onRelinked: (message: string) => void
}

const RelinkInvoiceModal = ({ tenantId, invoiceId, invoiceNumber, onClose, onRelinked }: Props) => {
  const [selectedQboInvoiceId, setSelectedQboInvoiceId] = useState<string | undefined>(undefined)
  const candidatesQuery = useRelinkCandidatesQuery(tenantId, invoiceId)
  const mutation = useRelinkQuickBooksInvoiceMutation()

  const rows = candidatesQuery.isSuccess
    ? toRelinkCandidateRows(candidatesQuery.data.candidates, candidatesQuery.data.currentQboInvoiceId)
    : []
  const selectedId = selectedQboInvoiceId ?? getProposedCandidateId(rows)
  const selectedRow = rows.find(row => row.qboInvoiceId === selectedId)
  const customerName = candidatesQuery.data?.qboCustomerName ?? ''

  const handleRelink = () => {
    if (selectedRow === undefined) {
      return
    }
    mutation.mutate(
      { tenantId, invoiceId, qboInvoiceId: selectedRow.qboInvoiceId },
      {
        onSuccess: () => {
          onRelinked(getRelinkSuccessMessage(invoiceNumber, selectedRow.docNumber))
        },
      }
    )
  }

  const columns = [
    { title: 'QBO Invoice #', dataIndex: 'docNumber', key: 'docNumber' },
    { title: 'Total', dataIndex: 'total', key: 'total' },
    { title: 'Balance', dataIndex: 'balance', key: 'balance' },
    { title: 'Date', dataIndex: 'transactionDate', key: 'transactionDate' },
    {
      title: 'Matches',
      key: 'matches',
      render: (_: unknown, row: RelinkCandidateRow) => (
        <>
          {row.matchTags.map(tag => (
            <Tag color="green" key={tag}>{tag}</Tag>
          ))}
          {row.isCurrentLink && <Tag>Current link</Tag>}
          {row.linkedToInvoiceNumber !== null && (
            <Tag color="orange">Linked to invoice {row.linkedToInvoiceNumber}</Tag>
          )}
        </>
      ),
    },
  ]

  return (
    <Modal
      open
      title={`Relink invoice ${invoiceNumber}`}
      okText="Relink"
      cancelText="Cancel"
      width={840}
      onCancel={onClose}
      onOk={handleRelink}
      okButtonProps={{ disabled: selectedRow === undefined }}
      confirmLoading={mutation.isLoading}
    >
      <Paragraph type="secondary">
        Lists the QuickBooks invoices billed to {customerName === '' ? "this invoice's customer" : customerName},
        ranked by how well they match invoice {invoiceNumber}. Confirm the invoice to relink to.
      </Paragraph>

      {candidatesQuery.isLoading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin tip="Searching QuickBooks..." />
        </div>
      )}

      {candidatesQuery.isError && (
        <Alert
          type="error"
          showIcon
          message="Failed to look up matching QuickBooks invoices"
          description={getRelinkCandidatesErrorMessage(candidatesQuery.error)}
        />
      )}

      {candidatesQuery.isSuccess && rows.length === 0 && (
        <Alert
          type="warning"
          showIcon
          message="No invoices found"
          description={getNoCandidatesMessage(candidatesQuery.data.qboCustomerName)}
        />
      )}

      {candidatesQuery.isSuccess && candidatesQuery.data.candidateListTruncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Candidate list is incomplete"
          description={candidateListTruncatedMessage}
        />
      )}

      {rows.length > 0 && (
        <Table
          columns={columns}
          dataSource={rows}
          size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedId === undefined ? [] : [selectedId],
            onChange: selectedRowKeys => setSelectedQboInvoiceId(selectedRowKeys[0] as string),
          }}
        />
      )}

      {mutation.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Relink failed"
          description={getRelinkErrorMessage(mutation.error)}
        />
      )}
    </Modal>
  )
}

export default RelinkInvoiceModal
